import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { scaleLinear } from 'd3-scale';

interface LiveSPLGaugeProps {
  value: number;          // Current SPL level (dB)
  peakValue?: number;     // Peak SPL level (optional)
  minValue?: number;      // Minimum scale value (default: 40)
  maxValue?: number;      // Maximum scale value (default: 120)
  label?: string;         // Gauge label
  size?: number;          // Gauge size in pixels (default: 280)
}

const LiveSPLGauge: React.FC<LiveSPLGaugeProps> = ({
  value,
  peakValue,
  minValue = 40,
  maxValue = 120,
  label = 'Sound Level',
  size = 280,
}) => {
  const [displayValue, setDisplayValue] = useState(value);

  // Smooth spring animation for value changes
  const springValue = useSpring(value, {
    stiffness: 60,
    damping: 15,
  });

  // Update display value smoothly
  useEffect(() => {
    const unsubscribe = springValue.on('change', (latest) => {
      setDisplayValue(latest);
    });
    return unsubscribe;
  }, [springValue]);

  // Update spring when value changes
  useEffect(() => {
    springValue.set(value);
  }, [value, springValue]);

  // Color scale based on SPL levels
  const getColor = (level: number): string => {
    if (level < 70) return '#10B981'; // Green
    if (level < 85) return '#F59E0B'; // Yellow
    if (level < 95) return '#F97316'; // Orange
    return '#EF4444'; // Red
  };

  // Calculate arc parameters
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = (size / 2) - 40;
  const strokeWidth = 20;

  // Arc spans from -135deg to +135deg (270deg total)
  const startAngle = -135;
  const endAngle = 135;
  const totalAngle = endAngle - startAngle;

  // Scale value to angle
  const valueScale = scaleLinear()
    .domain([minValue, maxValue])
    .range([startAngle, endAngle])
    .clamp(true);

  const currentAngle = valueScale(displayValue);
  const peakAngle = peakValue ? valueScale(peakValue) : currentAngle;

  // Convert angle to SVG path coordinates
  const polarToCartesian = (angle: number): { x: number; y: number } => {
    const angleInRadians = ((angle - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  // Create arc path
  const createArc = (startAngle: number, endAngle: number): string => {
    const start = polarToCartesian(endAngle);
    const end = polarToCartesian(startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    return [
      'M', start.x, start.y,
      'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y,
    ].join(' ');
  };

  // Background arc (full range)
  const backgroundArc = createArc(startAngle, endAngle);

  // Active arc (current value)
  const activeArc = createArc(startAngle, currentAngle);

  // Peak indicator position
  const peakPosition = polarToCartesian(peakAngle);

  // Threshold markers
  const thresholds = [
    { value: 70, label: '70', color: '#10B981' },
    { value: 85, label: '85', color: '#F59E0B' },
    { value: 95, label: '95', color: '#EF4444' },
  ];

  const currentColor = getColor(displayValue);

  return (
    <div className="relative inline-block" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background arc */}
        <path
          d={backgroundArc}
          fill="none"
          stroke="rgba(156, 163, 175, 0.2)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />

        {/* Gradient definition for active arc */}
        <defs>
          <linearGradient id={`gaugeGradient-${label}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.8" />
            <stop offset="50%" stopColor="#F59E0B" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#EF4444" stopOpacity="1" />
          </linearGradient>

          {/* Glow filter */}
          <filter id={`glow-${label}`}>
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Active arc with animation */}
        <motion.path
          d={activeArc}
          fill="none"
          stroke={currentColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          filter={`url(#glow-${label})`}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />

        {/* Peak indicator */}
        {peakValue && (
          <motion.circle
            cx={peakPosition.x}
            cy={peakPosition.y}
            r={6}
            fill={getColor(peakValue)}
            initial={{ scale: 0 }}
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
          />
        )}

        {/* Threshold markers */}
        {thresholds.map((threshold, index) => {
          const angle = valueScale(threshold.value);
          const position = polarToCartesian(angle);
          return (
            <g key={index}>
              <circle
                cx={position.x}
                cy={position.y}
                r={4}
                fill={threshold.color}
                opacity={0.6}
              />
              <text
                x={position.x}
                y={position.y - 15}
                textAnchor="middle"
                className="text-xs fill-gray-500 dark:fill-gray-400"
                fontSize="10"
              >
                {threshold.label}
              </text>
            </g>
          );
        })}

        {/* Center value display */}
        <text
          x={centerX}
          y={centerY - 10}
          textAnchor="middle"
          className="fill-gray-900 dark:fill-gray-100"
          style={{ fontSize: '48px', fontWeight: 'bold' }}
        >
          <motion.tspan
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {Math.round(displayValue)}
          </motion.tspan>
        </text>

        {/* dB label */}
        <text
          x={centerX}
          y={centerY + 20}
          textAnchor="middle"
          className="fill-gray-600 dark:fill-gray-400"
          style={{ fontSize: '18px', fontWeight: '500' }}
        >
          dB
        </text>

        {/* Label */}
        <text
          x={centerX}
          y={centerY + 45}
          textAnchor="middle"
          className="fill-gray-500 dark:fill-gray-500"
          style={{ fontSize: '14px' }}
        >
          {label}
        </text>
      </svg>

      {/* Pulsing glow effect for high values */}
      {displayValue > 95 && (
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background: `radial-gradient(circle, ${currentColor}20 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
          animate={{
            opacity: [0.3, 0.6, 0.3],
            scale: [0.95, 1.05, 0.95],
          }}
          transition={{
            repeat: Infinity,
            duration: 2,
            ease: 'easeInOut',
          }}
        />
      )}
    </div>
  );
};

export default LiveSPLGauge;
